import { useState, useEffect } from 'react';
import { avisos as avisosApi, conductores as conductoresApi } from '../services/api';
import {
    Bell, Send, AlertTriangle, AlertCircle, Info,
    Upload, X, FileText, Loader2, Check
} from 'lucide-react';

const PRIORIDADES = [
    { value: 'normal', label: 'Normal', icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { value: 'importante', label: 'Importante', icon: AlertCircle, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
    { value: 'urgente', label: 'Urgente', icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10' },
];

const Avisos = () => {
    const [formData, setFormData] = useState({
        titulo: '',
        contenido: '',
        prioridad: 'normal',
        para_todos: true,
        lineas: [],
        conductores: [],
        fecha_expiracion: '',
        enviar_push: true,
    });

    const [lineasDisponibles, setLineasDisponibles] = useState([]);
    const [conductoresDisponibles, setConductoresDisponibles] = useState([]);
    const [adjuntos, setAdjuntos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [savedAvisoId, setSavedAvisoId] = useState(null);

    useEffect(() => {
        fetchLineas();
        fetchConductores();
    }, []);

    const fetchLineas = async () => {
        try {
            const data = await avisosApi.getLineas();
            setLineasDisponibles(data);
        } catch (err) {
            console.error('Error fetching lineas:', err);
        }
    };

    const fetchConductores = async () => {
        try {
            const data = await conductoresApi.getAll(true); // Solo activos
            setConductoresDisponibles(data.list || data);
        } catch (err) {
            console.error('Error fetching conductores:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleLineaToggle = (lineaId) => {
        setFormData(prev => ({
            ...prev,
            lineas: prev.lineas.includes(lineaId)
                ? prev.lineas.filter(id => id !== lineaId)
                : [...prev.lineas, lineaId]
        }));
    };

    const handleConductorToggle = (conductorId) => {
        setFormData(prev => ({
            ...prev,
            conductores: prev.conductores.includes(conductorId)
                ? prev.conductores.filter(id => id !== conductorId)
                : [...prev.conductores, conductorId]
        }));
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Si no hay aviso guardado, primero crear borrador
        let avisoId = savedAvisoId;
        if (!avisoId) {
            try {
                const res = await avisosApi.create(formData);
                avisoId = res.id;
                setSavedAvisoId(avisoId);
            } catch (err) {
                setError('Error al crear el aviso');
                return;
            }
        }

        // Subir archivos
        setUploading(true);
        for (const file of files) {
            try {
                const adjunto = await avisosApi.uploadAdjunto(avisoId, file);
                setAdjuntos(prev => [...prev, { ...adjunto, nombre: file.name }]);
            } catch (err) {
                console.error('Error uploading file:', err);
                setError(`Error al subir ${file.name}`);
            }
        }
        setUploading(false);
        e.target.value = ''; // Reset input
    };

    const removeAdjunto = async (adjuntoId) => {
        try {
            await avisosApi.deleteAdjunto(adjuntoId);
            setAdjuntos(prev => prev.filter(a => a.id !== adjuntoId));
        } catch (err) {
            console.error('Error removing adjunto:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSending(true);

        try {
            let avisoId = savedAvisoId;

            if (avisoId) {
                // Actualizar borrador existente
                await avisosApi.update(avisoId, formData);
            } else {
                // Crear nuevo
                const res = await avisosApi.create(formData);
                avisoId = res.id;
            }

            // Publicar el aviso
            await avisosApi.publicar(avisoId);

            setSuccess(true);

            // Reset form after 2 seconds
            setTimeout(() => {
                setFormData({
                    titulo: '',
                    contenido: '',
                    prioridad: 'normal',
                    para_todos: true,
                    lineas: [],
                    conductores: [],
                    fecha_expiracion: '',
                    enviar_push: true,
                });
                setAdjuntos([]);
                setSavedAvisoId(null);
                setSuccess(false);
            }, 2000);

        } catch (err) {
            console.error('Error sending aviso:', err);
            setError(err.response?.data?.message || 'Error al enviar el aviso');
        } finally {
            setSending(false);
        }
    };

    const selectedPrioridad = PRIORIDADES.find(p => p.value === formData.prioridad);

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                    <Bell className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-white">Enviar Aviso</h1>
                    <p className="text-gray-400">Comunicados para conductores de TUVISA</p>
                </div>
            </div>

            {/* Success message */}
            {success && (
                <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-medium">¡Aviso enviado correctamente!</span>
                </div>
            )}

            {/* Error message */}
            {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400">{error}</span>
                    <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Card contenedor */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-6">

                    {/* Prioridad */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-3">Prioridad</label>
                        <div className="flex gap-3">
                            {PRIORIDADES.map(p => (
                                <button
                                    key={p.value}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, prioridad: p.value }))}
                                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${formData.prioridad === p.value
                                        ? `${p.bg} border-current ${p.color}`
                                        : 'border-white/10 text-gray-400 hover:border-white/20'
                                        }`}
                                >
                                    <p.icon className="w-5 h-5" />
                                    <span className="font-medium">{p.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Título */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Título del aviso</label>
                        <input
                            type="text"
                            name="titulo"
                            value={formData.titulo}
                            onChange={handleChange}
                            required
                            placeholder="Ej: Cambio de recorrido en línea 5"
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                        />
                    </div>

                    {/* Contenido */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Mensaje</label>
                        <textarea
                            name="contenido"
                            value={formData.contenido}
                            onChange={handleChange}
                            required
                            rows={5}
                            placeholder="Escribe el contenido del aviso..."
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all resize-none"
                        />
                    </div>

                    {/* Destinatarios */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-3">Destinatarios</label>
                        <div className="space-y-3">
                            <label className="flex items-center gap-3 p-3 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                                <input
                                    type="checkbox"
                                    name="para_todos"
                                    checked={formData.para_todos}
                                    onChange={handleChange}
                                    className="w-5 h-5 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                                />
                                <span className="text-gray-200">Todos los conductores</span>
                            </label>

                            {!formData.para_todos && (
                                <div className="space-y-6">
                                    {/* Selector de Líneas */}
                                    <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                                        <div className="flex items-center justify-between mb-3">
                                            <p className="text-sm font-medium text-cyan-400">Filtrar por línea:</p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {lineasDisponibles.map(linea => (
                                                <button
                                                    key={linea.id}
                                                    type="button"
                                                    onClick={() => handleLineaToggle(linea.id)}
                                                    className={`px-4 py-2 rounded-lg font-medium transition-all ${formData.lineas.includes(linea.id)
                                                        ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                                                        : 'bg-[#1a1a2e] border border-white/10 text-gray-300 hover:border-white/20 hover:bg-white/5'
                                                        }`}
                                                >
                                                    Línea {linea.numero}
                                                </button>
                                            ))}
                                            {lineasDisponibles.length === 0 && (
                                                <p className="text-gray-500 text-sm">No hay líneas configuradas</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Selector de Conductores específicos */}
                                    <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                                        <div className="flex items-center justify-between mb-3">
                                            <p className="text-sm font-medium text-orange-400">O enviar a conductores específicos:</p>
                                        </div>
                                        <div className="grid border border-white/10 rounded-lg max-h-[250px] overflow-y-auto bg-[#1a1a2e]">
                                            {conductoresDisponibles.map(conductor => (
                                                <label
                                                    key={conductor.id}
                                                    className="flex items-center gap-3 p-3 border-b border-white/5 last:border-0 hover:bg-white/5 cursor-pointer transition-colors"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.conductores?.includes(conductor.id) || false}
                                                        onChange={() => handleConductorToggle(conductor.id)}
                                                        className="w-4 h-4 rounded border-gray-600 text-orange-500 focus:ring-orange-500 bg-black/20"
                                                    />
                                                    <span className="text-gray-200">{conductor.nombre}</span>
                                                    {conductor.turno_inicial && (
                                                        <span className="text-xs text-gray-500 ml-auto border border-gray-600 rounded px-1">{conductor.turno_inicial}</span>
                                                    )}
                                                </label>
                                            ))}
                                            {conductoresDisponibles.length === 0 && (
                                                <div className="p-4 text-center">
                                                    <Loader2 className="w-5 h-5 text-gray-500 animate-spin mx-auto" />
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2 ml-1">
                                            * Los conductores seleccionados recibirán el aviso aunque no estén en las líneas marcadas arriba.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Archivos adjuntos */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-3">Archivos adjuntos</label>

                        {/* Lista de adjuntos */}
                        {adjuntos.length > 0 && (
                            <div className="mb-3 space-y-2">
                                {adjuntos.map(adj => (
                                    <div key={adj.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                                        <FileText className="w-5 h-5 text-cyan-400" />
                                        <span className="flex-1 text-gray-200 truncate">{adj.nombre_original || adj.nombre}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeAdjunto(adj.id)}
                                            className="text-gray-400 hover:text-red-400"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Botón subir */}
                        <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-all">
                            <input
                                type="file"
                                multiple
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={handleFileChange}
                                className="hidden"
                                disabled={uploading}
                            />
                            {uploading ? (
                                <>
                                    <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                                    <span className="text-gray-400">Subiendo...</span>
                                </>
                            ) : (
                                <>
                                    <Upload className="w-5 h-5 text-gray-400" />
                                    <span className="text-gray-400">Añadir archivos PDF o imágenes</span>
                                </>
                            )}
                        </label>
                    </div>

                    {/* Opciones adicionales */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Fecha expiración */}
                        <div>
                            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                                Fecha de expiración (opcional)
                            </label>
                            <input
                                type="datetime-local"
                                name="fecha_expiracion"
                                value={formData.fecha_expiracion}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-xl text-[var(--text-primary)] focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                            />
                            <p className="text-xs text-[var(--text-muted)] mt-1">
                                Después de esta fecha el aviso dejará de mostrarse
                            </p>
                        </div>

                        {/* Push notification */}
                        <div className="flex items-center">
                            <label className="flex items-center gap-3 p-4 bg-white/5 rounded-xl cursor-pointer w-full">
                                <input
                                    type="checkbox"
                                    name="enviar_push"
                                    checked={formData.enviar_push}
                                    onChange={handleChange}
                                    className="w-5 h-5 rounded border-gray-600 text-cyan-500 focus:ring-cyan-500"
                                />
                                <div>
                                    <span className="text-gray-200 font-medium">Enviar notificación push</span>
                                    <p className="text-xs text-gray-500">Los conductores recibirán una alerta en su móvil</p>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Botón enviar */}
                <button
                    type="submit"
                    disabled={sending || !formData.titulo || !formData.contenido}
                    className={`w-full flex items-center justify-center gap-3 py-4 rounded-xl font-semibold text-lg transition-all ${selectedPrioridad.value === 'urgente'
                        ? 'bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400'
                        : selectedPrioridad.value === 'importante'
                            ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400'
                            : 'bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400'
                        } text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                    {sending ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Enviando...
                        </>
                    ) : (
                        <>
                            <Send className="w-5 h-5" />
                            Publicar Aviso
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};

export default Avisos;
